import { twMerge } from "tailwind-merge";
import { Translate01 } from "untitledui-js/react";
import { languageDutch, languageEnglish } from "../../i18n";
import { ControlCenterButton } from "./ControlCenterButton";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getLanguageFromPathname } from "@utils/lang";
import { useTranslation } from "react-i18next";
import { useUpdatePreference } from "@hooks/useUpdatePreference";

interface LanguageSwitchProps {
  translatedSlug: string;
  languagePreference: string;
  buttonVariant?: "light" | "dark";
  className?: string;
}

export const LanguageSwitch = ({
  translatedSlug,
  languagePreference,
  buttonVariant = "light",
  className,
}: LanguageSwitchProps) => {
  const { mutate: updatePreferences, isPending } = useUpdatePreference();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Detect language from pathname because :lang can be any slug on subpages
  const currentLanguage =
    searchParams.get("_storyblok_lang") ||
    getLanguageFromPathname(location.pathname);

  /**
   * Event handlers
   */
  const handleNavigateToLanguage = (e: React.MouseEvent<HTMLButtonElement>) => {
    // get the id of the clicked button
    const selectedLanguage = e.currentTarget.id;

    if (selectedLanguage !== currentLanguage) {
      // Update the current URL to the translated slug.
      void navigate(translatedSlug, { replace: true });
    }

    // Add a timeout to ensure we change the language after the navigation.
    globalThis.setTimeout(() => {
      void i18n.changeLanguage(selectedLanguage).then(() => {
        // Update user language preference
        updatePreferences({
          languagePreference: selectedLanguage,
        });
      });
    }, 100);
  };

  return (
    <div className={twMerge("flex gap-2", className)}>
      <ControlCenterButton
        id={languageDutch}
        icon={<Translate01 />}
        text="Nederlands"
        onClick={handleNavigateToLanguage}
        active={languageEnglish !== languagePreference}
        ariaLabel="Vertaal naar Nederlands"
        disabled={isPending}
        buttonVariant={buttonVariant}
      />
      <ControlCenterButton
        id={languageEnglish}
        icon={<Translate01 />}
        text="English"
        onClick={handleNavigateToLanguage}
        active={languageEnglish === languagePreference}
        ariaLabel="Translate to English"
        disabled={isPending}
        buttonVariant={buttonVariant}
      />
    </div>
  );
};
